import { api } from '@api'
import type { FetchParams, PaginatedResponse } from './types'
import { usePaginate } from './usePaginate'

export interface Memory {
  id: string
  mediaUrl: string
  mediaMimeType: string
  description: string | null
  mediaType: string
  sharedWithAccountIds: string[]
  albumId: string | null
  createdByAccountId: string
  createdAt: string
  updatedAt: string
}

// Per request: the Souvenirs tab loads every page, so fewer round trips than the API's 10.
const PAGE_SIZE = 30

const fetchMyMemories = async (
  params?: FetchParams,
): Promise<PaginatedResponse<Memory>> => {
  const queryParams = new URLSearchParams()
  if (params?.pageParam) {
    queryParams.append('page', String(params.pageParam))
  }
  queryParams.append('limit', String(PAGE_SIZE))

  const url = `/memories/me?${queryParams.toString()}`
  const { data } = await api.get<PaginatedResponse<Memory>>(url)
  return data
}

export const usePaginateMyMemories = (options?: { enabled?: boolean }) => {
  return usePaginate<Memory>('myMemories', fetchMyMemories, undefined, {
    enabled: options?.enabled,
  })
}

const fetchContactMemories = async (
  params?: FetchParams & { contactId?: string },
): Promise<PaginatedResponse<Memory>> => {
  const queryParams = new URLSearchParams()
  if (params?.pageParam) {
    queryParams.append('page', String(params.pageParam))
  }
  queryParams.append('limit', String(PAGE_SIZE))

  const { data } = await api.get<PaginatedResponse<Memory>>(
    `/contacts/${params?.contactId}/memories?${queryParams.toString()}`,
  )
  return data
}

/**
 * The souvenirs shared between me and a contact, both ways: mine shared with
 * them and theirs shared with me. For a contact without account, pass the id
 * of the card.
 */
export const usePaginateContactMemories = (contactId?: string) => {
  return usePaginate<Memory>(
    'contact-memories',
    fetchContactMemories,
    { contactId },
    { enabled: !!contactId, nextPageWhenFull: true },
  )
}

// Keep backward-compatible export
export const usePaginateMemories = usePaginateMyMemories
